import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  alpha,
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatFinance } from "../../../../utils/formatters";

// Animation variants
const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

// Color constants
const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
];

const MicroMarketAnalysis = ({ data, loading }) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "order_count",
    direction: "desc",
  });
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSort = (key) => {
    let direction = "desc";
    if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc";
    }
    setSortConfig({ key, direction });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 500,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!data || !data.neighborhood_data || data.neighborhood_data.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Sem dados disponíveis para o período selecionado
        </Typography>
      </Box>
    );
  }

  // Filter and sort data
  const filteredData = searchTerm
    ? data.neighborhood_data.filter((item) =>
        item.neighborhood.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : data.neighborhood_data;

  const sortedData = [...filteredData].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  // Prepare data for charts
  const topNeighborhoodsByRevenue = [...data.neighborhood_data]
    .sort((a, b) => b.total_revenue - a.total_revenue)
    .slice(0, 10);

  const topNeighborhoodsByOrders = [...data.neighborhood_data]
    .sort((a, b) => b.order_count - a.order_count)
    .slice(0, 10);

  const successRateData = [...data.neighborhood_data]
    .sort((a, b) => b.success_rate - a.success_rate)
    .slice(0, 10)
    .map((item) => ({
      name: item.neighborhood,
      successRate: parseFloat(item.success_rate.toFixed(1)),
      cancellationRate: parseFloat(item.cancellation_rate.toFixed(1)),
    }));

  const avgOrderValueData = [...data.neighborhood_data]
    .sort((a, b) => b.avg_order_value - a.avg_order_value)
    .slice(0, 8)
    .map((item) => ({
      name: item.neighborhood,
      value: parseFloat(item.avg_order_value.toFixed(2)),
    }));

  return (
    <Grid container spacing={3} sx={{ p: 2 }}>
      <Grid item xs={12} component={motion.div} variants={itemVariants}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <LocationCityIcon sx={{ mr: 1, color: "primary.main" }} />
              Análise de Micro Mercados
            </Box>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Métricas de desempenho detalhadas por bairro para o período de{" "}
            {new Date(data.date_range.start).toLocaleDateString()} até{" "}
            {new Date(data.date_range.end).toLocaleDateString()}
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ mb: 3 }}
          >
            <Tab
              icon={<AttachMoneyIcon />}
              label="Faturamento"
              iconPosition="start"
            />
            <Tab
              icon={<LocalShippingIcon />}
              label="Volume de Pedidos"
              iconPosition="start"
            />
            <Tab
              icon={<CheckCircleIcon />}
              label="Taxa de Sucesso"
              iconPosition="start"
            />
            <Tab
              icon={<TrendingUpIcon />}
              label="Ticket Médio"
              iconPosition="start"
            />
          </Tabs>

          <Box sx={{ height: 400 }}>
            {tabValue === 0 && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topNeighborhoodsByRevenue}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="neighborhood"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                  />
                  <YAxis
                    tickFormatter={(value) => formatFinance(value, true)}
                  />
                  <Tooltip
                    formatter={(value) => [formatFinance(value), "Faturamento"]}
                  />
                  <Bar dataKey="total_revenue" fill="#8884d8">
                    {topNeighborhoodsByRevenue.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}

            {tabValue === 1 && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topNeighborhoodsByOrders}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="neighborhood"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip
                    formatter={(value) => [`${value} pedidos`, "Quantidade"]}
                  />
                  <Bar dataKey="order_count" fill="#82ca9d">
                    {topNeighborhoodsByOrders.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}

            {tabValue === 2 && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={successRateData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                  />
                  <YAxis domain={[0, 100]} />
                  <Tooltip formatter={(value) => [`${value}%`, ""]} />
                  <Legend />
                  <Bar
                    dataKey="successRate"
                    name="Taxa de Sucesso"
                    fill="#4caf50"
                  />
                  <Bar
                    dataKey="cancellationRate"
                    name="Taxa de Cancelamento"
                    fill="#f44336"
                  />
                </BarChart>
              </ResponsiveContainer>
            )}

            {tabValue === 3 && (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={avgOrderValueData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={150}
                    fill="#8884d8"
                    label={({ name, value }) =>
                      `${name}: ${formatFinance(value)}`
                    }
                  >
                    {avgOrderValueData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [
                      formatFinance(value),
                      "Ticket Médio",
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1">
              Dados Detalhados por Bairro
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Buscar bairro..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === "neighborhood"}
                      direction={
                        sortConfig.key === "neighborhood"
                          ? sortConfig.direction
                          : "asc"
                      }
                      onClick={() => handleSort("neighborhood")}
                    >
                      Bairro
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === "order_count"}
                      direction={
                        sortConfig.key === "order_count"
                          ? sortConfig.direction
                          : "desc"
                      }
                      onClick={() => handleSort("order_count")}
                    >
                      Pedidos
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === "total_revenue"}
                      direction={
                        sortConfig.key === "total_revenue"
                          ? sortConfig.direction
                          : "desc"
                      }
                      onClick={() => handleSort("total_revenue")}
                    >
                      Faturamento
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === "avg_order_value"}
                      direction={
                        sortConfig.key === "avg_order_value"
                          ? sortConfig.direction
                          : "desc"
                      }
                      onClick={() => handleSort("avg_order_value")}
                    >
                      Ticket Médio
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === "success_rate"}
                      direction={
                        sortConfig.key === "success_rate"
                          ? sortConfig.direction
                          : "desc"
                      }
                      onClick={() => handleSort("success_rate")}
                    >
                      Taxa de Sucesso
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === "cancellation_rate"}
                      direction={
                        sortConfig.key === "cancellation_rate"
                          ? sortConfig.direction
                          : "desc"
                      }
                      onClick={() => handleSort("cancellation_rate")}
                    >
                      Taxa de Cancelamento
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === "avg_delivery_tax"}
                      direction={
                        sortConfig.key === "avg_delivery_tax"
                          ? sortConfig.direction
                          : "desc"
                      }
                      onClick={() => handleSort("avg_delivery_tax")}
                    >
                      Taxa de Entrega Média
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: alpha(theme.palette.action.hover, 0.1),
                      },
                    }}
                  >
                    <TableCell component="th" scope="row">
                      {row.neighborhood}
                    </TableCell>
                    <TableCell align="right">
                      {row.order_count.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {formatFinance(row.total_revenue)}
                    </TableCell>
                    <TableCell align="right">
                      {formatFinance(row.avg_order_value)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color:
                          row.success_rate > 90
                            ? "success.main"
                            : row.success_rate > 75
                            ? "warning.main"
                            : "error.main",
                      }}
                    >
                      {row.success_rate.toFixed(1)}%
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color:
                          row.cancellation_rate < 5
                            ? "success.main"
                            : row.cancellation_rate < 15
                            ? "warning.main"
                            : "error.main",
                      }}
                    >
                      {row.cancellation_rate.toFixed(1)}%
                    </TableCell>
                    <TableCell align="right">
                      {formatFinance(row.avg_delivery_tax)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default MicroMarketAnalysis;
